<template>
  <!-- the 32px content indent and the top margin exist to clear this shell's
       own left nav rail. horizon/midnight don't render that rail (its
       destinations live in the section nav), so there they are dead space and
       these pages sat further right than every other horizon page. same
       treatment alerts_shell already carries. -->
  <div class="rs-shell" :class="{ 'rs-shell--flush': horizonNavActive }">
    <div class="rs-layout">

      <!-- Collapsible left nav -->
      <div v-if="!horizonNavActive" class="rs-nav-wrapper" :class="{ 'rs-nav-collapsed': navCollapsed }">
        <div class="rs-nav-inner">

          <!-- Dashboard overview link -->
          <router-link
            :to="{ name: 'admin_tools_dashboard' }"
            class="rs-nav-item rs-nav-home"
            :class="{ active: $route.name === 'admin_tools_dashboard' }"
          >
            <i class="fa fa-th-large rs-nav-home-icon"></i>
            Dashboard
          </router-link>

          <!-- Flat list of admin tool links -->
          <div class="rs-nav-cat">
            <router-link
              v-for="item in navItems"
              :key="item.route"
              :to="{ name: item.route }"
              class="rs-nav-sub-item"
              :class="{ active: $route.name === item.route }"
            >
              {{ item.name }}
            </router-link>
          </div>

          <!-- preview tools mirror the dashboard's preview cards; these routes
               render outside the shell, so following one leaves admin tools -->
          <div class="rs-nav-cat-label">Preview</div>
          <div class="rs-nav-cat">
            <router-link
              v-for="item in previewItems"
              :key="item.route"
              :to="{ name: item.route, query: item.query }"
              class="rs-nav-sub-item"
            >
              {{ item.name }}
            </router-link>
          </div>

        </div>

        <button
          class="rs-nav-toggle"
          @click="toggleNav"
          :title="navCollapsed ? 'Expand menu' : 'Collapse menu'"
          :aria-label="navCollapsed ? 'Expand menu' : 'Collapse menu'"
        >
          <i class="fa" :class="navCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'"></i>
        </button>
      </div>

      <!-- Page content -->
      <div class="rs-content">
        <router-view />
      </div>

    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import horizonNavMixin from '@/nav/horizon_nav_mixin'

export default {
  name: 'admin_shell',
  mixins: [horizonNavMixin],
  data() {
    return {
      navCollapsed: false,
      navUserToggled: false,
    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    navItems() {
      return [
        { name: 'Summary Dashboard',      route: 'admin_dashboard' },
        { name: 'Manage Users',           route: 'admin_manage_users' },
        { name: 'Settings History',       route: 'admin_settings_history' },
        { name: 'Account & Billing Stats', route: 'tenant_manager' },
        { name: 'API Usage',               route: 'api_usage_dashboard' },
        { name: 'SMS Usage',               route: 'sms_usage_dashboard' },
        { name: 'Process Manager',         route: 'process_manager_list' },
        { name: 'Actuals Simulator',       route: 'actuals_simulator' },
        { name: 'URL Shortener',           route: 'url_shortener' },
        { name: 'Cache Manager',           route: 'cache_manager' },
      ]
    },
    previewItems() {
      return [
        // ops events left this group when it became a main nav item for staff
        // as well as customers
        { name: 'Today Board',            route: 'ops_board' },
        { name: 'Vehicle Status Manager', route: 'vsm_board', query: { tenant: this.user.tenant } },
        { name: 'Departure Board',        route: 'departure_board', query: { tenant: this.user.tenant } },
      ]
    },
  },
  mounted() {
    this._onResize = () => {
      // Keep the nav responsive to window width unless the user has explicitly toggled it
      if (this.navUserToggled) return
      this.navCollapsed = window.innerWidth < 1600
    }
    window.addEventListener('resize', this._onResize)
    this._onResize()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize)
  },
  methods: {
    toggleNav() {
      this.navCollapsed = !this.navCollapsed
      this.navUserToggled = true
    },
  },
}
</script>

<style scoped>
/* ── Outer layout ───────────────────────────────────────────────────── */
.rs-layout {
  display: flex;
  align-items: flex-start;
}

/* ── Nav wrapper (handles collapse transition) ──────────────────────── */
.rs-nav-wrapper {
  position: relative;
  display: flex;
  flex-shrink: 0;
}

.rs-nav-inner {
  width: 250px;
  min-width: 250px;
  overflow: hidden;
  transition: width 0.22s ease, min-width 0.22s ease;
  background-color: #e8eef5;
  border-right: 2px solid #dee2e6;
  padding: 12px 0;
}

.rs-nav-collapsed .rs-nav-inner {
  width: 0;
  min-width: 0;
  border-right: none;
  padding: 0;
}

/* ── Dashboard home link ────────────────────────────────────────────── */
.rs-nav-home {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  border-bottom: 2px solid #c8d8e8;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  transition: background 0.12s;
}

.rs-nav-home:hover {
  background-color: #d0dfee;
  text-decoration: none !important;
}

.rs-nav-home.active {
  background-color: var(--fi-rail-accent) !important;
  color: white !important;
  border-bottom-color: #f0f0f0;
}

.rs-nav-home-icon {
  margin-right: 7px;
  font-size: 12px;
}

/* ── Nav items ──────────────────────────────────────────────────────── */
.rs-nav-cat {
  padding-top: 4px;
}

.rs-nav-cat-label {
  margin-top: 14px;
  padding: 6px 16px 2px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6b8299;
  white-space: nowrap;
}

.rs-nav-sub-item {
  display: block;
  padding: 8px 16px 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #333 !important;
  text-decoration: none !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-left: 3px solid transparent;
  position: relative;
  transition: background 0.1s, border-color 0.1s;
}

.rs-nav-cat .rs-nav-sub-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 0;
  height: 1px;
  background-color: #c8d8e8;
}

.rs-nav-sub-item:hover {
  background-color: #dce8f5;
  text-decoration: none !important;
}

.rs-nav-sub-item.active {
  background-color: #dce8f5;
  border-left-color: var(--fi-rail-accent);
  color: var(--fi-rail-accent) !important;
  font-weight: 600;
}

/* ── Collapse toggle nub ────────────────────────────────────────────── */
.rs-nav-toggle {
  position: absolute;
  top: 12px;
  right: -20px;
  width: 20px;
  height: 36px;
  background: #e8eef5;
  border: 1px solid #c8d8e8;
  border-left: none;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a6785;
  font-size: 11px;
  padding: 0;
  z-index: 10;
  transition: background 0.15s;
}

.rs-nav-toggle:hover {
  background: #d0dfee;
  color: var(--fi-rail-accent);
}

.rs-nav-toggle:focus {
  outline: none;
}

/* ── Content area ───────────────────────────────────────────────────── */
.rs-shell {
  margin-top: 20px;
}

.rs-content {
  flex: 1;
  min-width: 0;
  padding-left: 32px;
}

/* no in-page nav rail to clear (see the template note): the pages inside
   supply their own gutters, so hand the space back to them */
.rs-shell--flush {
  margin-top: 0;
}

.rs-shell--flush .rs-content {
  padding-left: 0;
}
</style>
